import { createAudioPlayer, setAudioModeAsync } from "expo-audio"
import React, { useEffect, useImperativeHandle, useRef } from "react"
import { Platform } from "react-native"
import { useSelector } from "react-redux"

// Transient playback errors that are noise, not bugs. Swallow them silently
// to stop flooding Bugsnag (cf. cluster #1, ~17k events / 60d):
// - NotAllowedError: web autoplay policy blocks playback without a fresh
//   user gesture (4164 events / 252 users)
// - NotSupportedError: audio element fed an empty/invalid source
//   (3684 events / 692 users)
// - AbortError: play() interrupted by a new load (fast sequential calls)
const EXPECTED_PLAYBACK_ERRORS = new Set([
    "NotAllowedError",
    "NotSupportedError",
    "AbortError",
])

const isExpectedPlaybackError = (error) =>
    !!error && EXPECTED_PLAYBACK_ERRORS.has(error.name)

export const PlaySound = React.forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        play: playSound,
        stop: stopSound,
    }))

    const isLiveModeEnabled = useSelector(
        (state) => state.liveMode?.isLiveModeEnabled
    )

    const isLiveModeEnabledRef = useRef(isLiveModeEnabled)
    const soundRef = useRef(null)
    // Timers belonging to the player currently in soundRef. Without this,
    // stopping during the `delay` window leaves the deferred play() to fire on
    // an already-removed player that soundRef no longer tracks — an audible
    // sound nothing can stop, and in live mode a session never released.
    const timersRef = useRef([])

    useEffect(() => {
        isLiveModeEnabledRef.current = isLiveModeEnabled
    }, [isLiveModeEnabled])

    const playSound = async (soundFile, delay = 0, isLooping = false) => {
        if (!soundFile) return
        // Android stays silent during a live: expo-audio can only set the
        // interruption mode through setAudioModeAsync, whose Android side
        // resets the audio manager to MODE_NORMAL + speakerphone — dropping
        // LiveKit's MODE_IN_COMMUNICATION and its hardware echo cancellation.
        // Without that call, playing would take audio focus from the call.
        if (isLiveModeEnabledRef.current && Platform.OS === "android") return

        try {
            // During an active live on iOS, LiveKit owns the AVAudioSession —
            // leave it untouched, the sound mixes into the existing session.
            // Outside a live, set the regular mode (silent-mode playback, duck
            // other apps' audio).
            if (!isLiveModeEnabledRef.current) {
                await setAudioModeAsync({
                    allowsRecording: true,
                    interruptionMode: "duckOthers",
                    playsInSilentMode: true,
                    shouldPlayInBackground: true,
                })
            }

            // During a live, expo-audio must NOT release the shared AVAudioSession
            // when the sound finishes or is paused: its native module calls
            // AVAudioSession.setActive(false) once no expo-audio player is
            // playing, which kills LiveKit's audio I/O in both directions.
            const player = createAudioPlayer(soundFile, {
                keepAudioSessionActive: isLiveModeEnabledRef.current,
            })
            player.loop = isLooping
            soundRef.current = player
            // Timers of the player being replaced are intentionally left to
            // fire — they are what releases it.
            const timers = []
            timersRef.current = timers

            timers.push(
                setTimeout(() => {
                    try {
                        player.play()
                    } catch (e) {
                        if (!isExpectedPlaybackError(e)) {
                            console.log(
                                "PLAY SOUND ERROR (play)",
                                e?.toString()
                            )
                        }
                        return
                    }

                    if (!isLooping) {
                        timers.push(
                            setTimeout(() => {
                                try {
                                    player.remove()
                                } catch (e) {
                                    // Player already released — ignore.
                                }
                                if (soundRef.current === player) {
                                    soundRef.current = null
                                }
                            }, 6000)
                        )
                    }
                }, delay)
            )

            return player
        } catch (e) {
            if (isExpectedPlaybackError(e)) return null
            console.log("PLAY SOUND ERROR", e?.toString())
            return null
        }
    }

    const stopSound = async () => {
        timersRef.current.forEach(clearTimeout)
        timersRef.current = []
        if (!soundRef.current) return
        const player = soundRef.current
        soundRef.current = null
        try {
            player.pause()
        } catch (e) {
            if (!isExpectedPlaybackError(e)) {
                console.log("STOP SOUND ERROR", e?.toString())
            }
        }
        try {
            player.remove()
        } catch (e) {
            if (!isExpectedPlaybackError(e)) {
                console.log("UNLOAD SOUND ERROR", e?.toString())
            }
        }
    }
})
